/**
 * Admin V3 — Bon de commande detail page
 *
 * Source: GET /admin/bon-de-commande/:id → { bon_de_commande }  (ADMIN_PARTNER_READ).
 * Lifecycle (all POST = ADMIN_PARTNER_UPDATE):
 *   issue / sign / cancel / convert(+booking), Download PDF (read).
 *
 * RBAC: mutation affordances hidden unless can('admin:partner:update'); backend
 * @rbac_permission authoritative. Lineage (source devis / version / converted-to
 * booking) shown for traceability.
 */

import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ClipboardList, ArrowLeft, Download, CheckCircle2, PenLine, Ban, ArrowRightCircle, Loader2 } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { SectionCard, SmallPill, currency, bdcStatusMeta, formatAddressLines, cn } from '../../shared/visualTokens';
import useAdminQuery from '../../shared/hooks/useAdminQuery';
import useAdminMutation from '../../shared/hooks/useAdminMutation';
import usePermissions from '../../shared/hooks/usePermissions';
import usePdfDownload from '../../shared/hooks/usePdfDownload';
import { useToast } from '../../shared/hooks/useToast';
import { t, DEFAULT_LOCALE } from '../../i18n';

const PERM_UPDATE = 'admin:partner:update';

const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Intl.DateTimeFormat('fr-FR', { year: 'numeric', month: '2-digit', day: '2-digit' }).format(typeof iso === 'string' ? new Date(iso) : iso); }
  catch { return String(iso); }
};

const Field = ({ label, children, accent }) => (
  <div className="text-[12px]">
    <dt className="text-slate-400 font-semibold uppercase tracking-wider text-[10px]">{label}</dt>
    <dd className={cn('font-medium break-all', accent || 'text-slate-700')}>{children}</dd>
  </div>
);

const BdcDetailPage = () => {
  const locale = DEFAULT_LOCALE;
  const { bdcId } = useParams();
  const navigate = useNavigate();
  const { can, loading: permsLoading } = usePermissions();
  const { showSuccess, showError } = useToast();

  const { data, loading, error, refetch } = useAdminQuery(`/admin/bon-de-commande/${bdcId}`);
  const bdc = data?.bon_de_commande || null;

  const issueM = useAdminMutation(`/admin/bon-de-commande/${bdcId}/issue`, 'post');
  const signM = useAdminMutation(`/admin/bon-de-commande/${bdcId}/sign`, 'post');
  const cancelM = useAdminMutation(`/admin/bon-de-commande/${bdcId}/cancel`, 'post');
  const convertM = useAdminMutation(`/admin/bon-de-commande/${bdcId}/convert`, 'post');
  const pdf = usePdfDownload();

  const [cancelOpen, setCancelOpen] = useState(false);
  const [reason, setReason] = useState('');

  const canUpdate = can(PERM_UPDATE);
  const s = bdc?.status;
  const isDraft = s === 'draft';
  const isIssued = s === 'issued';
  const isSigned = s === 'signed';
  const canConvert = isIssued || isSigned;
  const canCancel = ['draft', 'issued', 'signed'].includes(s);

  const onErr = (e) => showError(e?.response?.data?.detail || t('bdc.action.error', locale));
  const after = (msg) => ({ onSuccess: () => { showSuccess(msg); refetch(); }, onError: onErr });

  const handlePdf = () => pdf.download(`/admin/bon-de-commande/${bdcId}/pdf`, `bon-de-commande-${bdc?.bdc_number || 'draft'}.pdf`)
    .then((ok) => { if (!ok) showError(pdf.error || t('bdc.action.pdfError', locale)); });
  const handleIssue = () => issueM.mutate({}, after(t('bdc.action.issuedOk', locale)));
  const handleSign = () => signM.mutate({ method: 'operator_manual' }, after(t('bdc.action.signedOk', locale)));
  const handleConvert = () => convertM.mutate({}, {
    onSuccess: (res) => { showSuccess(t('bdc.action.convertedOk', locale).replace('{ref}', res?.booking?.booking_reference || '')); refetch(); },
    onError: onErr,
  });
  const handleCancel = () => cancelM.mutate({ reason }, { onSuccess: () => { showSuccess(t('bdc.action.cancelledOk', locale)); setCancelOpen(false); setReason(''); refetch(); }, onError: onErr });

  const lines = Array.isArray(bdc?.line_items) ? bdc.line_items : [];
  const taxLabel = t('bdc.field.tax', locale).replace('{rate}', String(bdc?.indicative_tax_rate ?? 0));

  return (
    <SectionCard
      title={bdc?.bdc_number || t('bdc.detail.back', locale)} icon={ClipboardList} iconClass="bg-purple-100 text-purple-600"
      action={
        <Button variant="ghost" size="sm" onClick={() => navigate('/admin/billing/commande')} data-testid="adminv3-bdc-detail-back">
          <ArrowLeft className="h-4 w-4 mr-1.5" />{t('bdc.detail.back', locale)}
        </Button>
      }
    >
      {loading && !data && (
        <div className="flex items-center gap-2 text-slate-400 text-sm py-8 justify-center"><Loader2 className="h-4 w-4 animate-spin" />{t('bdc.detail.loading', locale)}</div>
      )}
      {error && !loading && (
        <div className="rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700" data-testid="adminv3-bdc-detail-error">{error}</div>
      )}

      {bdc && (
        <div className="space-y-5">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <SmallPill meta={bdcStatusMeta[s] || bdcStatusMeta.draft} />
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" size="sm" onClick={handlePdf} disabled={pdf.loading} data-testid="adminv3-bdc-pdf">
                {pdf.loading ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Download className="h-3.5 w-3.5 mr-1.5" />}{t('bdc.action.pdf', locale)}
              </Button>
              {/* F2: while the permissions fetch is in flight, show a stable spinner
                  placeholder so mutation buttons appear once (resolved) instead of
                  flickering in after the read-only PDF button. */}
              {permsLoading && (
                <span className="flex items-center px-2 text-slate-300" data-testid="adminv3-bdc-perms-loading">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                </span>
              )}
              {canUpdate && isDraft && (
                <Button variant="default" size="sm" onClick={handleIssue} disabled={issueM.loading} data-testid="adminv3-bdc-issue">
                  <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" />{t('bdc.action.issue', locale)}
                </Button>
              )}
              {canUpdate && isIssued && (
                <Button variant="default" size="sm" onClick={handleSign} disabled={signM.loading} data-testid="adminv3-bdc-sign">
                  <PenLine className="h-3.5 w-3.5 mr-1.5" />{t('bdc.action.sign', locale)}
                </Button>
              )}
              {canUpdate && canConvert && (
                <Button variant="default" size="sm" onClick={handleConvert} disabled={convertM.loading} data-testid="adminv3-bdc-convert">
                  <ArrowRightCircle className="h-3.5 w-3.5 mr-1.5" />{t('bdc.action.convert', locale)}
                </Button>
              )}
              {canUpdate && canCancel && (
                <Button variant="outline" size="sm" onClick={() => { setCancelOpen((v) => !v); setReason(''); }} className="border-rose-200 text-rose-700 hover:bg-rose-50" data-testid="adminv3-bdc-cancel-toggle">
                  <Ban className="h-3.5 w-3.5 mr-1.5" />{t('bdc.action.cancel', locale)}
                </Button>
              )}
            </div>
          </div>

          {cancelOpen && canUpdate && (
            <div className="rounded-xl border border-rose-200 bg-rose-50/50 p-4 space-y-2" data-testid="adminv3-bdc-cancel-form">
              <label className="text-[12px] font-semibold text-rose-700">{t('bdc.cancel.reason', locale)}</label>
              <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} className="w-full rounded-lg border border-rose-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:ring-2 focus:ring-rose-100" />
              <div className="flex gap-2">
                <Button variant="default" size="sm" onClick={handleCancel} disabled={cancelM.loading} className="bg-rose-600 hover:bg-rose-700" data-testid="adminv3-bdc-cancel-confirm">
                  {cancelM.loading && <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />}{t('bdc.cancel.confirm', locale)}
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setCancelOpen(false)}>{t('bdc.create.cancel', locale)}</Button>
              </div>
            </div>
          )}

          {/* Lineage / source devis */}
          <div className="rounded-xl border border-slate-200 p-4">
            <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-400 mb-3">{t('bdc.section.lineage', locale)}</div>
            <dl className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-3">
              <Field label={t('bdc.field.sourceDevis', locale)} accent="text-teal-700">
                {bdc.source_devis_id ? (
                  <button type="button" className="underline" onClick={() => navigate(`/admin/billing/devis/${bdc.source_devis_id}`)} data-testid="adminv3-bdc-lineage-devis">
                    {bdc.devis_number || bdc.source_devis_id}
                  </button>
                ) : '—'}
              </Field>
              <Field label={t('bdc.field.devisVersion', locale)}>v{bdc.devis_version ?? 1}</Field>
              {bdc.customer_po_reference && <Field label={t('bdc.field.poRef', locale)}>{bdc.customer_po_reference}</Field>}
              {bdc.converted_to_booking_id && <Field label={t('bdc.field.convertedToBooking', locale)} accent="text-teal-700">{bdc.converted_to_booking_id}</Field>}
            </dl>
          </div>

          {/* Customer */}
          <div className="rounded-xl border border-slate-200 p-4">
            <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-400 mb-2">{t('bdc.section.customer', locale)}</div>
            <div className="text-slate-900 font-medium">{(bdc.customer && (bdc.customer.company || bdc.customer.name)) || '—'}</div>
            <div className="text-[12px] text-slate-500">{bdc.customer?.email || ''}</div>
            <div className="text-[12px] text-slate-500 whitespace-pre-line">{formatAddressLines(bdc.customer?.address)}</div>
          </div>

          {/* Amounts */}
          <div className="rounded-xl border border-slate-200 p-4">
            <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-400 mb-3">{t('bdc.section.amounts', locale)}</div>
            <dl className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-3">
              <Field label={t('bdc.field.subtotal', locale)}>{currency(bdc.subtotal_ht ?? 0)}</Field>
              <Field label={taxLabel}>{currency(bdc.indicative_tax_amount ?? 0)}</Field>
              <Field label={t('bdc.field.total', locale)} accent="text-slate-900 font-bold">{currency(bdc.total_ttc_estimated ?? 0)}</Field>
              {bdc.requested_service_date && <Field label={t('bdc.field.serviceDate', locale)}>{fmtDate(bdc.requested_service_date)}</Field>}
            </dl>
          </div>

          {lines.length > 0 && (
            <div className="rounded-xl border border-slate-200 p-4">
              <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-400 mb-2">{t('bdc.section.lineItems', locale)} ({lines.length})</div>
              <pre className="rounded-lg bg-slate-50 border border-slate-200 p-2 text-[11px] font-mono text-slate-600 overflow-x-auto max-h-56">
{JSON.stringify(lines, null, 2)}
              </pre>
            </div>
          )}
        </div>
      )}
    </SectionCard>
  );
};

export default BdcDetailPage;
